const API = 'https://api.github.com';

function ghHeaders(token) {
  return {
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
    'Content-Type': 'application/json',
  };
}

// Accepts "owner/repo" or "https://github.com/owner/repo[.git]"
export function parseRepo(repoInput) {
  const clean = (repoInput || '')
    .replace(/^https?:\/\/github\.com\//, '')
    .replace(/\.git$/, '')
    .trim();
  const [owner, repo] = clean.split('/');
  return { owner: owner || '', repo: repo || '' };
}

export async function getRepoInfo(token, owner, repo) {
  const res = await fetch(`${API}/repos/${owner}/${repo}`, { headers: ghHeaders(token) });
  if (!res.ok) throw new Error(`GitHub getRepo: ${res.status}`);
  return res.json();
}

export async function listContents(token, owner, repo, path = '', branch = 'main') {
  const url = `${API}/repos/${owner}/${repo}/contents/${path}?ref=${encodeURIComponent(branch)}`;
  const res = await fetch(url, { headers: ghHeaders(token) });
  if (!res.ok) throw new Error(`GitHub listContents ${path}: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data)
    ? data.map(f => ({ name: f.name, path: f.path, type: f.type, size: f.size }))
    : [];
}

export async function readFile(token, owner, repo, path, branch = 'main') {
  const url = `${API}/repos/${owner}/${repo}/contents/${path}?ref=${encodeURIComponent(branch)}`;
  const res = await fetch(url, { headers: ghHeaders(token) });
  if (!res.ok) throw new Error(`GitHub readFile ${path}: ${res.status} ${res.statusText}`);
  const data = await res.json();
  // GitHub returns content as base64 with newlines
  const content = atob(data.content.replace(/\n/g, ''));
  return { content, sha: data.sha, path: data.path };
}

export async function pushFile(token, owner, repo, path, content, message, branch = 'main', sha = null) {
  // btoa with unicode support
  const encoded = btoa(unescape(encodeURIComponent(content)));
  const body = { message, content: encoded, branch };
  if (sha) body.sha = sha;
  const res = await fetch(`${API}/repos/${owner}/${repo}/contents/${path}`, {
    method: 'PUT',
    headers: ghHeaders(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`GitHub pushFile ${path}: ${res.status} ${err.message || res.statusText}`);
  }
  return res.json();
}

export async function createBranch(token, owner, repo, branchName, fromBranch = 'main') {
  const refRes = await fetch(
    `${API}/repos/${owner}/${repo}/git/ref/heads/${fromBranch}`,
    { headers: ghHeaders(token) },
  );
  if (!refRes.ok) throw new Error(`GitHub createBranch: cannot find ref heads/${fromBranch}`);
  const { object: { sha } } = await refRes.json();

  const res = await fetch(`${API}/repos/${owner}/${repo}/git/refs`, {
    method: 'POST',
    headers: ghHeaders(token),
    body: JSON.stringify({ ref: `refs/heads/${branchName}`, sha }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`GitHub createBranch ${branchName}: ${res.status} ${err.message || ''}`);
  }
  return res.json();
}

export async function createPR(token, owner, repo, title, body, head, base = 'main') {
  const res = await fetch(`${API}/repos/${owner}/${repo}/pulls`, {
    method: 'POST',
    headers: ghHeaders(token),
    body: JSON.stringify({ title, body, head, base }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`GitHub createPR: ${res.status} ${err.message || ''}`);
  }
  return res.json();
}

export async function createIssue(token, owner, repo, title, body = '', labels = []) {
  const res = await fetch(`${API}/repos/${owner}/${repo}/issues`, {
    method: 'POST',
    headers: ghHeaders(token),
    body: JSON.stringify({ title, body, labels }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`GitHub createIssue: ${res.status} ${err.message || ''}`);
  }
  return res.json();
}
